import React, { createContext, useContext, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  getAuth,
} from "firebase/auth";
import { auth } from "../firebase";
import { AllData, UserData } from "./types";
import { toast } from "react-toastify";
import { momentsApiClient, zzeehApiClient } from "./apiClient";
import Loader from "../components/ui/Loader";

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  userData: UserData | undefined;
  events: Array<AllData> | undefined;
  setUserData: React.Dispatch<React.SetStateAction<UserData | undefined>>;
  setEvents: React.Dispatch<React.SetStateAction<Array<AllData> | undefined>>;
  fetchUserData: (email: string | null | undefined) => Promise<void>;
  getAllEvents: () => Promise<void>;
  googleSignIn: () => Promise<boolean>;
  signOut: () => void;
}

interface FirebaseUser extends User {}
interface AuthContextProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  children,
}) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<UserData | undefined>();
  const [events, setEvents] = useState<Array<AllData> | undefined>();

  const fetchUserData = async (email: string | null | undefined) => {
    if (email) {
      try {
        const res = await zzeehApiClient.get(`/users/email/${email}`);
        if (res.status === 200) {
          setUserData(res.data);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const getAllEvents = async () => {
    try {
      const res = await momentsApiClient.get("/events");
      // console.log(res.data);
      if (res.status === 200) {
        setEvents(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Separate function to handle user creation in your backend
  const createOrUpdateUserInBackend = async (user: User) => {
    try {
      // Check if email and displayName are not null
      if (!user.email || !user.displayName) {
        throw new Error("User email or display name is missing.");
      }

      // Proceed with the request if checks pass
      const res = await zzeehApiClient.post(`/users`, {
        full_name: user.displayName,
        email: user.email,
        phone_number: user.phoneNumber || "",
        user_type: "zzeeh",
      });

      if (res?.status === 200 || res.status === 201) {
        toast.success("Sign In Successful!");
        console.log("User created/updated in backend.");
        // console.log(res);
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.info("Access requested. Please wait for approval.");
      } else {
        toast.error("Unexpected error occurred.");
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchUserData(currentUser?.email);
      } else {
        setUser(null);
        setUserData(undefined);
      }
      setLoading(false); // Set loading to false when auth state is determined
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const googleSignIn = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    try {
      const val = await signInWithPopup(auth, provider);

      // Handle user creation in the external system
      await createOrUpdateUserInBackend(val.user);
      await fetchUserData(val.user.email);

      // Return true if sign-in and data fetching were successful
      return true;
    } catch (error) {
      console.error("Google Sign-in Error:", error);
      // Return false if any error occurs
      return false;
    }
  };

  const signOut = () => {
    firebaseSignOut(auth);
    setUser(null);
    setUserData(undefined);
    setEvents(undefined);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        userData,
        events,
        setUserData,
        setEvents,
        fetchUserData,
        getAllEvents,
        googleSignIn,
        signOut,
      }}
    >
      {loading ? (
        <Loader /> // Render loading indicator while Firebase processes
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const UserAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};
